import i18n from "@/plugins/i18n";
import { router } from "@/router";
import { notification } from "antd";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

type HttpMethod = "GET" | "POST" | "PATCH" | "DELETE";

const defaultInstance = axios.create({
  timeout: 60000,
  baseURL: !import.meta.env.DEV ? import.meta.env.VITE_API_BASEURL : undefined,
  paramsSerializer: { indexes: null },
});

defaultInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  token && Object.assign(config.headers, { Authorization: `Bearer ${token}` });
  return config;
});

defaultInstance.interceptors.response.use(undefined, async (error) => {
  if (axios.isAxiosError(error)) {
    // Unauthorized
    if (error.response?.status === 401) {
      await router.navigate("/login");
    } else if (error.code === "ERR_NETWORK") {
      // TODO: remove static notification method
      notification.error({
        message: i18n.t("systems.Error.requestError"),
        description: i18n.t("systems.Error.networkError"),
        key: error.config?.url,
      });
    } else if (error.response && error.response.status >= 500) {
      // TODO: remove static notification method
      notification.error({
        message: i18n.t("systems.Error.requestError"),
        description: i18n.t("systems.Error.serverError"),
        key: error.config?.url,
      });
    }
  }

  throw error;
});

const HttpCache = new Map<string, any>();

/**
 * 封裝 Axios 的類別
 *
 * 使用 chaining method 來達到 open to configure for each request
 */
class HttpClient<T> {
  private url = "";
  private data: any = {};
  private params: any = {};
  private method: HttpMethod = "GET";
  private instance: AxiosInstance;
  private readonly config: AxiosRequestConfig = {};

  constructor(instance = defaultInstance) {
    this.instance = instance;
  }

  /** 設置 GET 方法、參數 */
  get(url: string, params = {}) {
    this.url = url;
    this.method = "GET";
    Object.assign(this.params, params);
    return this;
  }

  /** 設置 POST 方法、資料、參數 */
  post(url: string, data = {}, params = {}) {
    this.url = url;
    this.method = "POST";
    Object.assign(this.params, params);
    Object.assign(this.data, data);
    return this;
  }

  /** 設置 PATCH 方法、資料、參數 */
  patch(url: string, data = {}, params = {}) {
    this.url = url;
    this.method = "PATCH";
    Object.assign(this.params, params);
    Object.assign(this.data, data);
    return this;
  }

  /** 設置 DELETE 方法、參數 */
  delete(url: string, params = {}) {
    this.url = url;
    this.method = "DELETE";
    Object.assign(this.params, params);
    return this;
  }

  /** 為該次請求打上標籤，可避免取消重複請求 (HttpCanceler 尚未實作) */
  tag(tagName: string) {
    (this.config as unknown as { tag?: string }).tag = tagName;
    return this;
  }

  /** 指定使用 blob 接收回傳 */
  blob() {
    this.config.responseType = "blob";
    return this;
  }

  /** 指定使用 stream 接收回傳 */
  stream() {
    this.config.responseType = "stream";
    return this;
  }

  /** 指定 timeout 毫秒數 */
  timeout(millisecond: number) {
    this.config.timeout = millisecond;
    return this;
  }

  setParams(params: any) {
    Object.assign(this.params, params);
    return this;
  }

  /** 設定請求參數，與 `setParams` 相同 */
  setQuery(params: any) {
    Object.assign(this.params, params);
    return this;
  }

  getCachedKey() {
    const paramsJson = JSON.stringify(this.params);
    const dataJson = JSON.stringify(this.data);
    return `[${this.method}]${this.url}:${paramsJson}|${dataJson}`;
  }

  /** 取得上次快取結果 */
  getCachedResponse() {
    return HttpCache.get(this.getCachedKey()) as
      | AxiosResponse<T, any>
      | undefined;
  }

  /** 發送 Http 請求 */
  async run({ cached }: { cached?: boolean } = {}) {
    if (!this.instance) throw new Error("HttpClient instance is not set");

    const res = await this.instance.request<T>({
      url: this.url,
      method: this.method,
      data: this.data,
      params: this.params,
      ...this.config,
    });

    cached && HttpCache.set(this.getCachedKey(), res);

    return res;
  }
}

export const httpClient = <T>(instance?: AxiosInstance) =>
  new HttpClient<T>(instance);
